<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>{{ loadingError }}</p>
      </div>
    </b-alert>

    <!-- Add/Edit Lead Modal -->
    <add-lead
      v-if="showLeadForm"
      :edit-id="editLeadId"
      @modal-closed="closeLeadForm"
      @lead-saved="leadSavedHandler"
    />

    <!-- Action Buttons -->
    <b-row>
      <b-col align="right">
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="openLeadForm(null)"
        >
          Add Lead
        </b-button>
      </b-col>
    </b-row>

    <!-- Lead Table -->
    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #cell(party_name)="data">
        {{ data.item.party_name || '-' }}
      </template>
      <template #cell(area)="data">
        {{ data.item.area || '-' }}
      </template>
      <template #cell(owner_mobile)="data">
        <span v-if="data.item.owner_mobile && data.item.owner_mobile.length > 0">
          {{ data.item.owner_mobile.join(', ') }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(manager_mobile)="data">
        <span v-if="data.item.manager_mobile && data.item.manager_mobile.length > 0">
          {{ data.item.manager_mobile.join(', ') }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(emails)="data">
        <span v-if="data.item.emails && data.item.emails.length > 0">
          {{ data.item.emails.join(', ') }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(expected_monthly_turnover)="data">
        {{ data.item.expected_monthly_turnover ? formateNumber(data.item.expected_monthly_turnover) : '-' }}
      </template>
      <template #cell(retail_app_installed)="data">
        {{ data.item.retail_app_installed === true || data.item.retail_app_installed === 'true' ? 'Yes' : 'No' }}
      </template>
      <template #cell(status)="data">
        <b-badge :variant="getStatusVariant(data.item.status)">
          {{ data.item.status || '-' }}
        </b-badge>
      </template>
      <template #cell(drug_license_image)="data">
        <a
          v-if="data.item.drug_license_image"
          :href="data.item.drug_license_image"
          target="_blank"
        >
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="View Image"
            icon="ImageIcon"
            size="20"
            class="cursor-pointer text-primary"
          />
        </a>
        <span v-else>-</span>
      </template>
      <template #cell(actions)="data">
        <feather-icon
          icon="EditIcon"
          class="cursor-pointer text-primary"
          size="16"
          @click="openLeadForm(data.item.id)"
        />
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, BBadge, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import AddLead from './AddLead.vue'

export default {
  name: 'LeadList',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    BBadge,
    DetailedPagination,
    AddLead,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,

      results: [],
      tableFields: [
        { key: 'party_name', label: 'Party Name', sortable: true },
        { key: 'area', label: 'Area', sortable: true },
        { key: 'owner_mobile', label: 'Owner Mobile', sortable: false },
        { key: 'manager_mobile', label: 'Manager Mobile', sortable: false },
        { key: 'emails', label: 'Emails', sortable: false },
        {
          key: 'expected_monthly_turnover', label: 'Expected Turnover', sortable: true, class: 'text-right pr-2',
        },
        { key: 'retail_app_installed', label: 'Retail App?', sortable: false },
        { key: 'transport_type', label: 'Transport Type', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'drug_license_image', label: 'DL Image', sortable: false },
        { key: 'created_by_name', label: 'Created By', sortable: false },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      currentPage: 1,
      perPage: 20,
      totalItems: 0,
      sortBy: 'id',
      sortDesc: true,

      // Lead form
      showLeadForm: false,
      editLeadId: null,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters: {
      deep: true,
      handler() {
        if (this.currentPage !== 1) {
          this.currentPage = 1
        } else {
          this.fetchItems()
        }
      },
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    getStatusVariant(status) {
      if (status === 'success') return 'light-success'
      if (status === 'failed') return 'light-danger'
      if (status === 'pending') return 'light-warning'
      return 'light-secondary'
    },

    fetchItems() {
      this.loading = true
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }

      // Handle array params - send multiple params with the same key
      const queryParts = []
      Object.keys(params).forEach(key => {
        const value = params[key]
        if (Array.isArray(value)) {
          value.forEach(v => {
            queryParts.push(`${key}=${encodeURIComponent(v)}`)
          })
        } else if (value !== null && value !== undefined && value !== '') {
          queryParts.push(`${key}=${encodeURIComponent(value)}`)
        }
      })

      const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''

      axios.get(`/delivery/lead/${queryString}`)
        .then(res => {
          this.results = res.data.results || res.data
          this.totalItems = res.data.count || this.results.length
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    openLeadForm(id) {
      this.editLeadId = id
      this.showLeadForm = true
    },
    closeLeadForm() {
      this.showLeadForm = false
      this.editLeadId = null
    },
    leadSavedHandler() {
      this.closeLeadForm()
      this.fetchItems()
    },
  },
}
</script>
